import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Bug, History, Loader2 } from 'lucide-react';
import { MetaTags } from '../../components/seo/MetaTags';
import { CaptureSection } from '../../components/capture/CaptureSection';
import { CreditPurchaseModal } from '../../components/credits/CreditPurchaseModal';
import { CreditStatus } from '../../components/credits/CreditStatus';
import { useAuth } from '../../hooks/use-auth';
import { useToast } from '../../hooks/use-toast';
import { detectPlantDisease } from '../../lib/google-vision-ai';
import {
  deductDiseaseDetectionCredits,
  getUserCredits,
  purchaseCredits
} from '../../lib/credits';
import { getDiseaseHistory, saveDiseaseDetection } from '../../lib/firestore';
import { DiseaseReport } from '../../components/disease/DiseaseReport';
import { DiseaseHistoryItem } from '../../components/disease/DiseaseHistoryItem';
import { DiseaseHistoryModal } from '../../components/disease/DiseaseHistoryModal';
import { Analytics } from '../../lib/firebase-analytics';

// Helper function to extract plant name from analysis
function extractPlantName(result) {
  if (!result) return 'Unknown Plant';

  // For healthy plants, extract from recommendations
  if (result.status === 'healthy' && result.recommendations) {
    // Look for "Recommendations for [Plant Name]" pattern
    const recommendationsMatch = result.recommendations.match(
      /Recommendations for\s+([^:]+):/i
    );
    if (recommendationsMatch && recommendationsMatch[1]) {
      return recommendationsMatch[1].trim();
    }

    // Alternative pattern: Look for plant name at the start
    const plantNameMatch = result.recommendations.match(
      /^[^:]*?([A-Z][a-z]+ [a-z]+)/
    );
    if (plantNameMatch && plantNameMatch[1]) {
      return plantNameMatch[1].trim();
    }
  }

  // For disease detections, look in the analysis
  if (result.analysis) {
    // Look for the plant identification section
    const plantIdMatch = result.analysis.match(
      /Plant Identification:[\s\S]*?Species name[^:]*:(.*?)(?:\(|$)/i
    );
    if (plantIdMatch && plantIdMatch[1]) {
      return plantIdMatch[1].trim();
    }

    // Fallback: try to find any plant name in the first few lines
    const firstLines = result.analysis.split('\n').slice(0, 5);
    for (const line of firstLines) {
      if (line.includes('name') || line.includes('species')) {
        const nameMatch = line.match(/:\s*([^()\n]+)/);
        if (nameMatch && nameMatch[1]) {
          return nameMatch[1].trim();
        }
      }
    }
  }

  return 'Unknown Plant';
}

export function DiseaseDetection() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState('upload');
  const [imageData, setImageData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState({ regular: 0, premium: 0, total: 0 });
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDetection, setSelectedDetection] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  useEffect(() => {
    async function fetchData() {
      if (user) {
        try {
          setIsLoading(true);
          const [userCredits, detectionHistory] = await Promise.all([
            getUserCredits(user.uid),
            getDiseaseHistory(user.uid)
          ]);
          setCredits(userCredits);
          setHistory(detectionHistory);
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to fetch data',
            variant: 'destructive'
          });
        } finally {
          setIsLoading(false);
        }
      }
    }

    fetchData();
  }, [user, toast]);

  const handleImageCapture = async data => {
    // Check if user has enough credits before proceeding
    if (credits.total < 5) {
      Analytics.logFeatureInteraction(
        'disease_detection',
        'insufficient_credits'
      );
      toast({
        title: 'Insufficient Credits',
        description:
          'You need 5 credits to perform disease detection. Please purchase more credits to continue.',
        variant: 'destructive'
      });
      setIsPurchaseModalOpen(true);
      return;
    }

    try {
      setImageData(data);
      setIsAnalyzing(true);
      setError(null);
      setResult(null);

      // Track disease detection attempt
      Analytics.logFeatureInteraction('disease_detection', 'start_detection');

      // First deduct credits
      const updatedCredits = await deductDiseaseDetectionCredits(user.uid);
      setCredits(updatedCredits);

      // Track credit usage
      Analytics.logCreditUsage(5, 'disease_detection');

      const detectionResult = await detectPlantDisease(data, user.uid);
      setResult(detectionResult);

      // Track successful detection
      Analytics.logDiseaseDetection(true, {
        plant_name: extractPlantName(detectionResult),
        status: detectionResult.status
      });

      // Extract plant name from the result
      const plantName = extractPlantName(detectionResult);

      // Save the detection result with the extracted plant name
      await saveDiseaseDetection(user.uid, {
        imageUrl: data,
        result: detectionResult,
        status: detectionResult.status,
        plantName,
        createdAt: new Date()
      });

      // Refresh history
      const updatedHistory = await getDiseaseHistory(user.uid);
      setHistory(updatedHistory);
    } catch (error) {
      // Track detection error
      Analytics.logDiseaseDetectionError(error);

      setError(error.message);
      toast({
        title: 'Error',
        description:
          error.message || 'Failed to analyze plant. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handlePurchaseCredits = async amount => {
    try {
      await purchaseCredits(user.uid, amount);
      const newCredits = await getUserCredits(user.uid);
      setCredits(newCredits);
      toast({
        title: 'Success',
        description: `Successfully purchased ${amount} credits!`,
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to purchase credits',
        variant: 'destructive'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center justify-center p-2 bg-amber-100 rounded-full mb-4">
              <Bug className="w-6 h-6 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Plant Disease Detection
            </h1>
            <p className="text-gray-600">
              Upload a photo of your plant to identify diseases and get
              treatment recommendations
            </p>
          </motion.div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 text-amber-600 animate-spin mb-4" />
              <p className="text-gray-600">Loading disease detection data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaTags
        title="Plant Disease Detection"
        description="Detect and diagnose plant diseases using our AI-powered tool."
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-transparent pt-24 px-4 pb-32">
        <div className="max-w-3xl mx-auto space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center p-2 bg-amber-100 rounded-full mb-4">
              <Bug className="w-6 h-6 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Plant Disease Detection
            </h1>
            <p className="text-gray-600">
              Upload a photo of your plant to identify diseases and get
              treatment recommendations
            </p>
          </motion.div>

          <CreditStatus
            credits={credits.total}
            onBuyCredits={() => setIsPurchaseModalOpen(true)}
          />

          <div className="bg-white rounded-lg shadow-lg p-6">
            <CaptureSection
              mode={mode}
              setMode={setMode}
              onImageCapture={handleImageCapture}
              imageData={imageData}
            />

            {isAnalyzing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-6 bg-white rounded-lg p-6 flex flex-col items-center justify-center"
              >
                <Loader2 className="w-8 h-8 text-amber-600 animate-spin mb-4" />
                <p className="text-gray-600">Analyzing your plant...</p>
              </motion.div>
            )}

            {error && !isAnalyzing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-6 bg-red-50 rounded-lg p-6 flex items-start gap-3"
              >
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-red-800 mb-1">
                    Analysis Failed
                  </h3>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              </motion.div>
            )}

            {result && !isAnalyzing && !error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-6"
              >
                <DiseaseReport result={result} />
              </motion.div>
            )}
          </div>

          {/* History Section */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <History className="w-5 h-5 text-amber-600" />
              <h2 className="text-xl font-semibold">Detection History</h2>
            </div>

            <div className="space-y-4">
              {history.length === 0 ? (
                <div className="text-center text-gray-600 py-8">
                  <Bug className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No disease detections yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {history.map(detection => (
                    <DiseaseHistoryItem
                      key={detection.id}
                      detection={detection}
                      onClick={detection => {
                        setSelectedDetection(detection);
                        setIsHistoryModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <CreditPurchaseModal
          isOpen={isPurchaseModalOpen}
          onClose={() => setIsPurchaseModalOpen(false)}
          onPurchase={handlePurchaseCredits}
        />

        <DiseaseHistoryModal
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          detection={selectedDetection}
        />
      </div>
    </>
  );
}
